// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  FLIGHT_AI_SUMMARY_GENERATOR_VERSION,
  FLIGHT_AI_SUMMARY_PROMPT_VERSION,
  FLIGHT_AI_SUMMARY_SCHEMA_VERSION,
} from "../core/schema.js";

// AI summary panel support: metadata helpers, graph-spec wrappers, canvas
// drawing, scroll-into-view, work-notice toasts, and progress-indicator
// state. Extracted from app.js.
//
// The panel render function (renderAiSummaryPanel) and the network loader
// (loadAiSummaryForSelectedFlight) stay in app.js because they mutate
// global UI state (selectedHealthKey, selectedCylinderNumber,
// aiSummaryState) and recursively re-render. This factory exposes
// everything they depend on — the AI summary subsystem's "internal"
// supporting helpers.
//
// The factory owns the four work-notice timers as closure state. The
// caller never touches them, only invokes the public helpers.

export function createAiSummaryPanelHelpers({
  // state getters / setters
  getAiSummaryState,
  setAiSummaryState,
  getAiSummaryEvidenceExpanded,
  setAiSummaryEvidenceExpanded,
  getSelectedFlight,
  getFlightData,
  getHealthSummary,
  getFlightManifest,
  // helpers
  escapeHtml,
  selectedDatabaseFlightId,
  reportPanelIsActive,
  activateDashboardAiReviewPanel,
  renderAiSummaryPanel,
  loadAiSummaryForSelectedFlight,
  // analysis helpers (for graph specs context)
  cylinderKeys,
  availableSeriesKeys,
  calculateHealthSummary,
  detectionWindowsForHealthKey,
  compactHealthNote,
  getHealthMetricLabels,
  healthSummaryOptions,
  // graph-spec FromContext imports
  aiSummaryAvailableSeriesFromContext,
  aiSummaryThermalPeerKeysFromContext,
  aiSummarySeriesForHealthKeyFromContext,
  aiSummaryGraphSpecFromContext,
  aiSummaryGraphSpecsFromContext,
  // chart drawing helpers
  chartBounds,
  seriesMeta,
  colors,
  drawEmptyChartMessage,
  drawGrid,
  drawSelectedDetectionWindow,
  drawLine,
  drawInlineLegend,
  drawDetectionWindowLabel,
}) {
  // Closure state for the work-notice and progress indicator. App.js
  // never touches these directly; everything goes through the public
  // helpers.
  let aiSummaryWorkNoticeTimer = null;
  let aiSummaryWorkAction = null;
  let aiSummaryWorkStartedAt = 0;
  let aiSummaryWorkElapsedTimer = null;

  function aiSummaryAvailableForEntry(entry = getSelectedFlight()) {
    return Boolean(selectedDatabaseFlightId(entry) && entry?.database?.analysisRunId);
  }

  function flightAiSummaryAvailable(entry) {
    return Boolean(entry?.database?.aiSummaryId);
  }

  // A saved summary is stale when the evidence_packet_id it was generated
  // against differs from the flight's current evidence_packet_id (which
  // changes whenever the flight is re-analyzed). Returns false when no
  // summary exists, so callers should pair this with flightAiSummaryAvailable.
  function flightAiSummaryStale(entry) {
    if (!flightAiSummaryAvailable(entry)) return false;
    if (
      entry?.database?.aiSummarySchemaVersion
      && entry.database.aiSummarySchemaVersion !== FLIGHT_AI_SUMMARY_SCHEMA_VERSION
    ) return true;
    if (
      entry?.database?.aiSummaryGeneratorVersion
      && entry.database.aiSummaryGeneratorVersion !== FLIGHT_AI_SUMMARY_GENERATOR_VERSION
    ) return true;
    if (
      entry?.database?.aiSummaryPromptVersion
      && entry.database.aiSummaryPromptVersion !== FLIGHT_AI_SUMMARY_PROMPT_VERSION
    ) return true;
    const summaryPacketId = entry?.database?.aiSummaryEvidencePacketId ?? null;
    const currentPacketId = entry?.database?.currentEvidencePacketId ?? null;
    if (!summaryPacketId) return false;
    if (!currentPacketId) return true;
    if (summaryPacketId !== currentPacketId) return true;
    const summaryEvidenceSha = entry?.database?.aiSummarySourceEvidenceSha256 ?? null;
    const currentEvidenceSha = entry?.database?.currentEvidenceSha256 ?? null;
    return Boolean(summaryEvidenceSha && currentEvidenceSha && summaryEvidenceSha !== currentEvidenceSha);
  }

  function maybeLoadCachedAiSummaryForSelectedFlight() {
    syncAiSummaryStateWithSelectedFlight();
    const flightId = selectedDatabaseFlightId();
    const selectedFlight = getSelectedFlight();
    if (!flightId || !flightAiSummaryAvailable(selectedFlight)) return;
    // Defer while the flight-loading overlay is up; loadFlightLog re-fires the
    // panel once it clears, so the AI gauge never stacks with the flight loader.
    if (document.querySelector("#view-dashboard")?.classList.contains("is-flight-loading")) return;
    const aiSummaryState = getAiSummaryState();
    if (aiSummaryState.flightId === flightId && (aiSummaryState.status === "loading" || aiSummaryState.status === "preparing" || aiSummaryState.payload)) return;
    void loadAiSummaryForSelectedFlight({ showProgressNotice: false });
  }

  function syncAiSummaryStateWithSelectedFlight() {
    const flightId = selectedDatabaseFlightId();
    const aiSummaryState = getAiSummaryState();
    if (!flightId || (aiSummaryState.flightId && aiSummaryState.flightId !== flightId)) {
      setAiSummaryEvidenceExpanded(false);
      stopAiSummaryProgressNotice();
      setAiSummaryState({ flightId: "", status: "idle", payload: null, error: "" });
    }
  }

  function updateManifestAiSummaryMetadata(entry, aiSummary, { stale = false } = {}) {
    if (!entry?.database || !aiSummary?.id) return;
    const updates = {
      aiSummaryId: aiSummary.id,
      aiSummaryGeneratedAt: aiSummary.generated_at ?? aiSummary.summary_json?.generator?.generatedAt ?? entry.database.aiSummaryGeneratedAt ?? null,
      aiSummaryModel: aiSummary.model ?? aiSummary.summary_json?.generator?.model ?? entry.database.aiSummaryModel ?? null,
      aiSummaryEvidencePacketId: aiSummary.evidence_packet_id ?? entry.database.aiSummaryEvidencePacketId ?? null,
      aiSummarySchemaVersion: aiSummary.schema_version ?? aiSummary.summary_json?.schemaVersion ?? entry.database.aiSummarySchemaVersion ?? null,
      aiSummaryGeneratorVersion: aiSummary.generator_version ?? aiSummary.summary_json?.generator?.version ?? entry.database.aiSummaryGeneratorVersion ?? null,
      aiSummaryPromptVersion: aiSummary.prompt_version ?? aiSummary.summary_json?.promptVersion ?? aiSummary.summary_json?.generator?.promptVersion ?? entry.database.aiSummaryPromptVersion ?? null,
      aiSummarySourceEvidenceSha256: aiSummary.source_evidence_sha256 ?? entry.database.aiSummarySourceEvidenceSha256 ?? null,
    };
    if (!stale && updates.aiSummaryEvidencePacketId) {
      updates.currentEvidencePacketId = updates.aiSummaryEvidencePacketId;
      if (updates.aiSummarySourceEvidenceSha256) updates.currentEvidenceSha256 = updates.aiSummarySourceEvidenceSha256;
    }
    Object.assign(entry.database, updates);
    const manifestEntry = getFlightManifest().find((item) => item.id === entry.id);
    if (manifestEntry?.database) Object.assign(manifestEntry.database, updates);
  }

  function aiSummaryGraphSpecsContext() {
    return {
      flightData: getFlightData(),
      cylinderKeys,
      availableSeriesKeys,
      healthSummary: getHealthSummary(),
      calculateHealthSummary,
      detectionWindowsForHealthKey,
      compactHealthNote,
      healthMetricLabels: getHealthMetricLabels(),
      healthSummaryOptions,
    };
  }

  function aiSummaryAvailableSeries(keys = []) {
    return aiSummaryAvailableSeriesFromContext(keys, { availableSeriesKeys });
  }

  function aiSummaryThermalPeerKeys(prefix, targetCylinder = null, limit = 6) {
    return aiSummaryThermalPeerKeysFromContext(prefix, {
      flightData: getFlightData(),
      cylinderKeys,
      targetCylinder,
      limit,
    });
  }

  function aiSummarySeriesForHealthKey(healthKey, metric, windows = []) {
    return aiSummarySeriesForHealthKeyFromContext(healthKey, metric, windows, aiSummaryGraphSpecsContext());
  }

  function aiSummaryGraphSpec(id, sectionLabel, evidenceKeys, evidenceText, options = {}) {
    return aiSummaryGraphSpecFromContext(id, sectionLabel, evidenceKeys, evidenceText, options, aiSummaryGraphSpecsContext());
  }

  function aiSummaryGraphSpecs(summary) {
    return aiSummaryGraphSpecsFromContext(summary, aiSummaryGraphSpecsContext());
  }

  function prepareAiSummaryGraphCanvas(canvas) {
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(320, Math.round((rect.width || 320) * 2));
    const height = Math.max(180, Math.round((rect.height || 180) * 2));
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    return canvas.getContext("2d");
  }

  function drawAiSummaryEvidenceGraph(canvas, spec) {
    const ctx = prepareAiSummaryGraphCanvas(canvas);
    const bounds = chartBounds(canvas, 30, { leftPadding: 34, rightPadding: 14, topPadding: 22, bottomPadding: 28 });
    const series = (spec?.series ?? [])
      .map((key) => ({ key, meta: seriesMeta[key] ?? { label: key, color: colors.blue } }))
      .filter(({ key }) => spec.data.some((point) => Number.isFinite(point[key])));
    if (!spec?.data?.length || !series.length) {
      drawEmptyChartMessage(ctx, canvas, bounds, "No trace values available");
      return;
    }
    drawGrid(ctx, canvas, bounds, []);
    drawSelectedDetectionWindow(ctx, spec.data, bounds, spec.windows);
    series.forEach(({ key, meta }, index) => {
      drawLine(ctx, spec.data, key, meta, bounds, {
        color: meta.color,
        independentScale: true,
        width: index === 0 ? 2.5 : 1.6,
      });
    });
    drawInlineLegend(
      ctx,
      bounds,
      series.slice(0, 5).map(({ meta }) => meta.label),
      series.slice(0, 5).map(({ meta }) => meta.color),
    );
    drawDetectionWindowLabel(ctx, spec.data, bounds, spec.windows);
  }

  function drawAiSummaryGraphs(specs = {}) {
    document.querySelectorAll("[data-ai-summary-graph]").forEach((canvas) => {
      const spec = specs[canvas.dataset.aiSummaryGraph];
      if (!spec) return;
      drawAiSummaryEvidenceGraph(canvas, spec);
    });
  }

  function scrollAiSummaryPanelIntoView() {
    const panel = document.querySelector("#aiSummaryPanel");
    if (!panel || panel.hidden) return;
    window.requestAnimationFrame(() => {
      panel.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function aiSummaryElapsedLabel(startedAt = aiSummaryWorkStartedAt) {
    if (!startedAt) return "0:00 elapsed";
    const seconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${String(seconds % 60).padStart(2, "0")} elapsed`;
  }

  function aiWorkIconMarkup(status = "loading") {
    if (status === "ready") {
      return `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M20 6L9 17l-5-5" />
      </svg>
    `;
    }
    if (status === "error") {
      return `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 8v5" />
        <path d="M12 17h.01" />
        <path d="M10.3 4.6 2.7 18a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 4.6a2 2 0 0 0-3.4 0z" />
      </svg>
    `;
    }
    return `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 3l1.7 5.1L19 10l-5.3 1.9L12 17l-1.7-5.1L5 10l5.3-1.9z" />
      <path d="M18 15l.8 2.2L21 18l-2.2.8L18 21l-.8-2.2L15 18l2.2-.8z" />
    </svg>
  `;
  }

  function showAiWorkNotice({
    status = "loading",
    title = "",
    message = "",
    actionLabel = "",
    action = null,
    autoDismissMs = 4200,
  } = {}) {
    const notice = document.querySelector("#aiSummaryWorkNotice");
    if (!notice) return;
    if (aiSummaryWorkNoticeTimer) {
      clearTimeout(aiSummaryWorkNoticeTimer);
      aiSummaryWorkNoticeTimer = null;
    }
    aiSummaryWorkAction = typeof action === "function" ? action : null;
    notice.hidden = !title && !message;
    notice.classList.toggle("is-ready", status === "ready");
    notice.classList.toggle("is-error", status === "error");
    notice.setAttribute("role", status === "error" ? "alert" : "status");
    notice.innerHTML = `
    <span class="ai-work-notice-icon" aria-hidden="true">${aiWorkIconMarkup(status)}</span>
    <div class="ai-work-notice-copy">
      <strong>${escapeHtml(title)}</strong>
      <p>${escapeHtml(message)}</p>
    </div>
    ${actionLabel && aiSummaryWorkAction ? `<button class="ai-work-notice-action" type="button" data-ai-work-notice-action>${escapeHtml(actionLabel)}</button>` : ""}
    <button class="ai-work-notice-dismiss" type="button" data-ai-work-notice-dismiss aria-label="Dismiss AI summary notification">
      <span aria-hidden="true">&times;</span>
    </button>
  `;
    if (autoDismissMs > 0) {
      aiSummaryWorkNoticeTimer = setTimeout(() => {
        hideAiWorkNotice();
      }, autoDismissMs);
    }
  }

  function hideAiWorkNotice() {
    const notice = document.querySelector("#aiSummaryWorkNotice");
    if (aiSummaryWorkNoticeTimer) {
      clearTimeout(aiSummaryWorkNoticeTimer);
      aiSummaryWorkNoticeTimer = null;
    }
    aiSummaryWorkAction = null;
    if (!notice) return;
    notice.hidden = true;
    notice.innerHTML = "";
    notice.classList.remove("is-ready", "is-error");
  }

  function aiSummaryWorkActionRef() {
    return aiSummaryWorkAction;
  }

  function renderAiSummaryWorkIndicator() {
    const chip = document.querySelector("#aiSummaryWorkChip");
    if (!chip) return;
    const aiSummaryState = getAiSummaryState();
    const loading = (aiSummaryState.status === "loading" || aiSummaryState.status === "preparing") && aiSummaryState.flightId === selectedDatabaseFlightId();
    chip.hidden = !loading;
    if (!loading) {
      chip.innerHTML = "";
      return;
    }
    chip.innerHTML = `
    <span class="ai-work-spinner" aria-hidden="true"></span>
    <strong>AI summary running</strong>
    <span>${escapeHtml(aiSummaryElapsedLabel())}</span>
  `;
  }

  function startAiSummaryProgressNotice(flightId) {
    aiSummaryWorkStartedAt = Date.now();
    if (aiSummaryWorkElapsedTimer) clearInterval(aiSummaryWorkElapsedTimer);
    aiSummaryWorkElapsedTimer = setInterval(renderAiSummaryWorkIndicator, 1000);
    renderAiSummaryWorkIndicator();
    showAiWorkNotice({
      status: "loading",
      title: "AI summary started",
      message: "Reading this flight's data and writing your summary. Large logs can take up to 2 minutes.",
      autoDismissMs: 10000,
    });
    return flightId;
  }

  function stopAiSummaryProgressNotice() {
    if (aiSummaryWorkElapsedTimer) {
      clearInterval(aiSummaryWorkElapsedTimer);
      aiSummaryWorkElapsedTimer = null;
    }
    aiSummaryWorkStartedAt = 0;
    renderAiSummaryWorkIndicator();
  }

  function finishAiSummaryProgressNotice(status, message = "") {
    stopAiSummaryProgressNotice();
    if (status === "ready") {
      if (reportPanelIsActive("ai-review")) {
        hideAiWorkNotice();
        renderAiSummaryPanel();
        return;
      }
      showAiWorkNotice({
        status: "ready",
        title: "AI summary ready",
        message: "Your flight summary is ready.",
        actionLabel: "View summary",
        action: () => {
          if (!reportPanelIsActive("ai-review")) activateDashboardAiReviewPanel();
          renderAiSummaryPanel();
          scrollAiSummaryPanelIntoView();
        },
        autoDismissMs: 7800,
      });
      return;
    }
    if (status === "error") {
      showAiWorkNotice({
        status: "error",
        title: "AI summary did not finish",
        message: message || "We couldn't finish your summary. Please try again.",
        actionLabel: aiSummaryAvailableForEntry() ? "Try again" : "",
        action: aiSummaryAvailableForEntry() ? () => loadAiSummaryForSelectedFlight({ force: true, scrollIntoView: true }) : null,
        autoDismissMs: 0,
      });
    }
  }

  function updateAiSummaryButtonState() {
    syncAiSummaryStateWithSelectedFlight();
    renderAiSummaryWorkIndicator();
    const button = document.querySelector("#action-ai-summary");
    if (!button) return;
    const label = button.querySelector(".button-label");
    const aiSummaryState = getAiSummaryState();
    const loading = aiSummaryState.status === "loading" || aiSummaryState.status === "preparing";
    const available = flightAiSummaryAvailable(getSelectedFlight()) || aiSummaryAvailableForEntry();
    button.disabled = loading || !available;
    button.classList.toggle("is-generating", loading);
    button.title = available
      ? "Generate a plain-language summary of this flight's engine data"
      : "Available once this flight is saved and analyzed";
    const text = loading ? "Summarizing" : "AI Summary";
    if (label) label.textContent = text;
    else button.textContent = text;
  }

  function aiSummaryLoadingMarkup(status = getAiSummaryState().status) {
    const preparing = status === "preparing";
    // Reuses the flight-loading overlay's gauge-card styling (.dashboard-loading-*)
    // so AI generation and flight loading share one visual language.
    return `
    <div class="dashboard-loading-card ai-summary-loading-card" role="status" aria-live="polite">
      <div class="dashboard-loading-instrument" aria-hidden="true">
        <div class="dashboard-loading-sweep"></div>
        <div class="dashboard-loading-needle"></div>
      </div>
      <div class="dashboard-loading-copy">
        <p class="eyebrow">AI Flight Summary</p>
        <h2>Generating AI summary</h2>
        <p>Going through this flight's results and writing the summary. Large logs can take up to 2 minutes.</p>
      </div>
      <ol class="dashboard-loading-steps" aria-label="AI summary generation progress">
        <li class="is-complete">Flight data gathered</li>
        <li class="${preparing ? "is-complete" : "is-active"}">Writing the summary</li>
        <li class="${preparing ? "is-active" : ""}">Almost ready</li>
      </ol>
    </div>
  `;
  }

  function waitForVisibleAiSummaryPreparingStep() {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        window.setTimeout(resolve, 2000);
      });
    });
  }

  return {
    aiSummaryAvailableForEntry,
    flightAiSummaryAvailable,
    flightAiSummaryStale,
    maybeLoadCachedAiSummaryForSelectedFlight,
    syncAiSummaryStateWithSelectedFlight,
    updateManifestAiSummaryMetadata,
    aiSummaryGraphSpecsContext,
    aiSummaryAvailableSeries,
    aiSummaryThermalPeerKeys,
    aiSummarySeriesForHealthKey,
    aiSummaryGraphSpec,
    aiSummaryGraphSpecs,
    prepareAiSummaryGraphCanvas,
    drawAiSummaryEvidenceGraph,
    drawAiSummaryGraphs,
    scrollAiSummaryPanelIntoView,
    aiSummaryElapsedLabel,
    aiWorkIconMarkup,
    showAiWorkNotice,
    hideAiWorkNotice,
    aiSummaryWorkActionRef,
    renderAiSummaryWorkIndicator,
    startAiSummaryProgressNotice,
    stopAiSummaryProgressNotice,
    finishAiSummaryProgressNotice,
    updateAiSummaryButtonState,
    aiSummaryLoadingMarkup,
    waitForVisibleAiSummaryPreparingStep,
  };
}
